// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/NonRefundableBadge.tsx
// 🔧 PURPOSE: Warning badge shown when a booking is non-refundable
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

interface NonRefundableBadgeProps {
  isNonRefundable?: boolean;
  variant?: 'full' | 'compact';
  className?: string;
}

const NonRefundableBadge: React.FC<NonRefundableBadgeProps> = ({
  isNonRefundable,
  variant = 'full',
  className = '',
}) => {
  const { t } = useTranslation();

  if (isNonRefundable !== true) return null;

  if (variant === 'compact') {
    return (
      <div
        role="alert"
        className={`flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl ${className}`}
      >
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-200">
          {t('myStay.nonRefundable.compact')}
        </p>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className={`flex items-start gap-2.5 p-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-300 dark:border-amber-700 rounded-xl ${className}`}
    >
      <div className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-xs leading-tight">
          {t('myStay.nonRefundable.title')}
        </h3>
        <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5 leading-snug">
          {t('myStay.nonRefundable.description')}
        </p>
      </div>
    </div>
  );
};

export default NonRefundableBadge;
